import React from "react";
import { Check } from "lucide-react";
import { business, homePreOwned, faqs } from "../mock/mock";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/siteConfig";
import { webPageSchema, breadcrumbSchema, faqSchema } from "../seo/schema";
// (LocalBusiness + WebSite are defined once globally in public/index.html)

import CircleArrowLink from "../components/common/CircleArrowLink";
import Reveal from "../components/common/Reveal";
import ElfsightWidget from "../components/common/ElfsightWidget";
import ServiceMarquee from "../components/sections/ServiceMarquee";
import WhyChooseUs from "../components/sections/WhyChooseUs";
import Testimonials from "../components/sections/Testimonials";
import FAQ from "../components/sections/FAQ";
import LocationCTA from "../components/sections/LocationCTA";

const heroBg =
  "https://framerusercontent.com/images/DlTJ0wjuV9VQB8sWaScg060WfY.webp?width=2048&height=1152";

const Home = () => {
  return (
    <>
      <Seo
        title={PAGE_SEO["/"].title}
        description={PAGE_SEO["/"].description}
        path="/"
        schema={[
          webPageSchema({ ...PAGE_SEO["/"], path: "/" }),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
          faqSchema(faqs),
        ]}
      />
      {/* ===================== HERO ===================== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img
          src={heroBg}
          alt="Phone repair close-up"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

        <div className="tnf-container relative pt-28 pb-16">
          <div className="max-w-2xl">
            <h1 className="font-display text-white font-extrabold leading-[1.04] text-[44px] sm:text-[60px] lg:text-[68px]">
              5-Star Repair for Cell Phones, Tablets, Computers & Consoles in
              Freehold, NJ.
            </h1>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CircleArrowLink to="/repair-estimate" variant="white">
                Start a repair
              </CircleArrowLink>
              <a
                href={business.phoneRaw}
                className="text-white/90 text-[15px] font-medium hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                or call {business.phone}
              </a>
            </div>

            <p className="text-white/75 text-[16px] sm:text-[17px] mt-7 max-w-md leading-relaxed border-l-2 border-white/30 pl-4">
              Get an instant quote and a guaranteed fix for any device. Faster,
              cheaper, and safer than the manufacturer.
            </p>
          </div>
        </div>

        {/* Small Elfsight Google reviews badge */}
        <div className="absolute bottom-7 right-5 lg:right-10 hidden sm:block w-[230px]">
          <ElfsightWidget appId="9dec219c-8fe8-459b-8a71-a9435d6f8867" />
        </div>
      </section>

      {/* ===================== LOGO LOOP (brands we service) ===================== */}
      <section className="py-10 lg:py-12 bg-white border-b border-black/5">
        <div className="tnf-container">
          <ElfsightWidget appId="418cddbf-bbf2-4ae6-9679-c064bf34554c" />
        </div>
      </section>

      {/* ===================== SERVICE RANGE ===================== */}
      <section className="py-20 lg:py-28">
        <div className="tnf-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="tnf-eyebrow">Our Services</span>
            <h2 className="font-display text-[32px] sm:text-[44px] font-extrabold text-[#0e0e10] mt-3 leading-[1.08]">
              Our Full Service Range
            </h2>
            <p className="text-[#3b3b3b]/80 text-[16px] mt-3">
              From cracked screens to dead consoles — one certified, multi-device
              expert in the heart of Freehold.
            </p>
          </Reveal>

          {/* Draggable, equal-height service cards (auto-scrolls, pause on hover) */}
          <Reveal>
            <ServiceMarquee />
          </Reveal>
        </div>
      </section>

      {/* ===================== PRE-OWNED TEASER ===================== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="tnf-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal className="relative">
              <div className="rounded-[28px] overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.12)]">
                <img
                  src={homePreOwned.image}
                  alt="Certified pre-owned devices"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
              </div>
              <div className="absolute -bottom-6 -right-2 sm:right-6 bg-[#0e0e10] text-white rounded-2xl px-6 py-4 shadow-xl">
                <div className="font-display text-[26px] font-extrabold leading-none">
                  6-Month
                </div>
                <div className="text-[13px] text-white/70">Warranty included</div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <span className="tnf-eyebrow">— {homePreOwned.eyebrow}</span>
              <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold text-[#0e0e10] mt-3 leading-[1.08]">
                {homePreOwned.title}
              </h2>
              <p className="text-[#3b3b3b]/80 text-[16px] mt-4 leading-relaxed">
                {homePreOwned.text}
              </p>

              <ul className="mt-6 space-y-3">
                {homePreOwned.points.map((p) => (
                  <li key={p} className="flex items-center gap-3">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(5,165,213,0.12)" }}
                    >
                      <Check size={14} color="var(--tnf-blue)" strokeWidth={3} />
                    </span>
                    <span className="text-[15px] font-medium text-[#0e0e10]">
                      {p}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <CircleArrowLink to="/pre-owned-devices" variant="blue">
                  Learn More
                </CircleArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== WHY CHOOSE ===================== */}
      <WhyChooseUs />

      {/* ===================== TESTIMONIALS ===================== */}
      <Testimonials />

      {/* ===================== FAQ ===================== */}
      <FAQ
        items={faqs}
        eyebrow="FAQs"
        title="Freehold's Device Repair Questions, Answered"
        subtitle="Everything you need to know about repairs, warranties and visiting us inside the Freehold Raceway Mall."
        className="bg-white"
      />

      {/* ===================== LOCATION ===================== */}
      <LocationCTA />
    </>
  );
};

export default Home;
